import React from 'react';
import { Source } from '../data/responses';

interface SourceCitationProps {
  sources: Source[];
}

export const SourceCitation: React.FC<SourceCitationProps> = ({ sources }) => {
  if (!sources || sources.length === 0) return null;

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.95) return 'text-green-600 bg-green-50';
    if (confidence >= 0.90) return 'text-blue-600 bg-blue-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.95) return '🎯 Very High';
    if (confidence >= 0.90) return '✓ High';
    return '~ Medium';
  };

  return (
    <div className="mt-4 space-y-2">
      <div className="text-xs font-bold text-purple-600 uppercase tracking-wide mb-2 flex items-center gap-2">
        <span>📚</span> Sources (Citation Tracking)
      </div>

      {sources.map((source, idx) => (
        <div
          key={idx}
          className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 rounded-lg p-3 hover:shadow-md transition-all cursor-pointer group"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-sm text-gray-800">
                  {source.product}
                </span>
                <span className="inline-block px-2 py-0.5 bg-purple-600 text-white rounded text-[10px] font-bold">
                  v{source.version}
                </span>
              </div>

              <div className="text-xs text-gray-600 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-purple-600 font-semibold">📄 {source.docType}</span>
                  {source.page && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span>Page {source.page}</span>
                    </>
                  )}
                </div>

                {source.section && (
                  <div className="text-gray-500">
                    <span className="font-semibold">Section:</span> {source.section}
                  </div>
                )}
              </div>
            </div>

            {/* Confidence Score */}
            <div className="flex flex-col items-end gap-1">
              <div className={`px-2 py-1 rounded-full text-[10px] font-bold ${getConfidenceColor(source.confidence)}`}>
                {getConfidenceBadge(source.confidence)}
              </div>
              <div className="text-[11px] font-mono text-gray-500">
                {(source.confidence * 100).toFixed(0)}%
              </div>
            </div>
          </div>

          {/* Hover action */}
          <div className="mt-2 text-[10px] text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity">
            🔍 Click to preview document
          </div>
        </div>
      ))}

      {/* Summary */}
      <div className="mt-3 p-2 bg-white rounded border border-gray-200">
        <div className="text-[10px] text-gray-500 text-center">
          ✓ All information verified from <span className="font-semibold text-purple-600">{sources.length} source document{sources.length > 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
};
